import Button from '@/components/Button.jsx';
import Drawer from '@/components/Drawer.jsx';
import EmptyStateNotice from '@/components/EmptyStateNotice.jsx';
import Skeleton from '@/components/Skeleton.jsx';
import { FiActivity, FiAlertTriangle, FiX, SlRefresh } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2.jsx';
import { getWorkflowLogExecutionContext } from '@/redux/actions/monitoringActions.js';
import selectors from '@/redux/selectors/selectors.js';
import { buildExecutionContextSourceItems, buildExecutionContextSummaryItems, formatExecutionContextTimelineDate, formatExecutionContextTimelineOffset, formatExecutionContextTimelineTime, getExecutionContextSourceLabel, getExecutionContextTimelineMemoryUsage, getExecutionContextTimelineMessage, getExecutionContextTimelineRawData, isCriticalExecutionContextTimelineRow, stringifyExecutionContextData } from './lib/workflowLogExecutionContext.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const WorkflowLogExecutionContextDrawer = ({ show, workflowLogId, onHide }) => {
  const cancelRef = useRef(null);
  const [loadedWorkflowLogId, setLoadedWorkflowLogId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const context = useSelector((state) => selectors.workflowLogExecutionContextById(state, workflowLogId));

  const summaryItems = useMemo(() => buildExecutionContextSummaryItems(context), [context]);
  const sourceItems = useMemo(() => buildExecutionContextSourceItems(context), [context]);
  const timelineRows = context?.timeline || [];
  const firstTimelineTimestamp = timelineRows[0]?.timestamp;
  const providerErrors = context?.errors || [];
  const providerWarnings = context?.warnings || [];

  useEffect(() => {
    return () => {
      if (typeof cancelRef.current?.cancel === 'function') {
        cancelRef.current.cancel('Component unmounted');
      }
    };
  }, []);

  const fetchContext = () => {
    if (!workflowLogId) {
      return;
    }

    setLoading(true);
    setError(null);
    getWorkflowLogExecutionContext({ workflowLogId, cancelRef }, (data, err) => {
      if (err) {
        setError(err?.message || 'Failed to load execution context.');
        setLoadedWorkflowLogId(workflowLogId);
      } else {
        setError(null);
        setLoadedWorkflowLogId(workflowLogId);
      }

      setLoading(false);
    });
  };

  useEffect(() => {
    if (show && workflowLogId && loadedWorkflowLogId !== workflowLogId) {
      if (context) {
        setError(null);
        setLoadedWorkflowLogId(workflowLogId);
        return;
      }

      fetchContext();
    }
  }, [show, workflowLogId, loadedWorkflowLogId, context]);

  return (
    <Drawer show={show} onHide={onHide} portal direction="right" className="workflow-log-execution-context-drawer">
      <div className="workflow-log-execution-context-drawer-inner">
        <div className="workflow-log-execution-context-drawer-head">
          <div className="workflow-log-execution-context-drawer-title-block">
            <div className="workflow-log-execution-context-drawer-title">Execution context</div>
            <div className="workflow-log-execution-context-drawer-subtitle">{workflowLogId}</div>
          </div>
          <Button className="ghost" size="sm" icon={FiX} onClick={onHide} title="Close" />
        </div>

        <div className="workflow-log-execution-context-drawer-actions">
          <Button className="outline" size="sm" icon={SlRefresh} onClick={fetchContext} loading={loading} disabled={!workflowLogId}>
            Refresh
          </Button>
          <span>Admin-only</span>
        </div>

        <div className="workflow-log-execution-context-drawer-body">
          {loading && !context && <Skeleton count={14} skeletonStyle={{ height: 32, borderRadius: 6, marginBottom: 8 }} containerStyle={{ padding: 0 }} />}

          {error && !loading && <EmptyStateNotice size="sm" title="Failed to load execution context" description={error} icon={FiAlertTriangle} />}

          {!loading && !error && context && (
            <>
              <section className="execution-context-section">
                <div className="execution-context-section-title">Summary</div>
                <div className="execution-context-kv-grid">
                  {summaryItems.map((item) => (
                    <div key={item.label} className="execution-context-kv">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="execution-context-section">
                <div className="execution-context-section-title">Sources</div>
                <div className="execution-context-kv-grid compact">
                  {sourceItems.map((item) => (
                    <div key={item.label} className="execution-context-kv">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <div className={`execution-context-provider-issues ${providerWarnings.length > 0 ? 'has-warnings' : ''}`}>
                <section className="execution-context-section">
                  <div className="execution-context-section-title">Errors</div>
                  {providerErrors.length === 0 && <div className="execution-context-muted">No provider errors.</div>}
                  {providerErrors.map((providerError, index) => (
                    <div key={`${providerError.source}-${providerError.phase}-${index}`} className="execution-context-error-row">
                      <IconV2 icon={FiAlertTriangle} size={14} />
                      <div>
                        <strong>{providerError.service || providerError.source}</strong>
                        <span>{providerError.message}</span>
                      </div>
                    </div>
                  ))}
                </section>

                {providerWarnings.length > 0 && (
                  <section className="execution-context-section">
                    <div className="execution-context-section-title">Warnings</div>
                    {providerWarnings.map((providerWarning, index) => {
                      const shouldPrefixCode = providerWarning.code && providerWarning.message && providerWarning.message !== providerWarning.code;
                      const warningMessage = shouldPrefixCode ? `${providerWarning.code}: ${providerWarning.message}` : providerWarning.message || providerWarning.code;

                      return (
                        <div key={`${providerWarning.source}-${providerWarning.phase}-${index}`} className="execution-context-warning-row">
                          <IconV2 icon={FiAlertTriangle} size={14} />
                          <div>
                            <strong>{providerWarning.service || providerWarning.source}</strong>
                            <span>{warningMessage}</span>
                          </div>
                        </div>
                      );
                    })}
                  </section>
                )}
              </div>

              <section className="execution-context-section">
                <div className="execution-context-section-title">Timeline</div>
                {timelineRows.length === 0 && <EmptyStateNotice size="sm" title="No timeline rows found" icon={FiActivity} />}
                <div className="execution-context-timeline">
                  {timelineRows.map((row, index) => {
                    const memoryUsage = getExecutionContextTimelineMemoryUsage(row);
                    const rawData = stringifyExecutionContextData(getExecutionContextTimelineRawData(row));
                    const isCritical = isCriticalExecutionContextTimelineRow(row);
                    const timelineOffset = formatExecutionContextTimelineOffset(row.timestamp, firstTimelineTimestamp);

                    return (
                      <div key={`${row.timestamp}-${row.service}-${row.event}-${index}`} className={`execution-context-timeline-row source-${row.source || 'unknown'} ${isCritical ? 'critical' : ''}`}>
                        <div className="execution-context-timeline-time">
                          <strong>{formatExecutionContextTimelineTime(row.timestamp)}</strong>
                          <span>{formatExecutionContextTimelineDate(row.timestamp)}</span>
                          {timelineOffset && <em>{timelineOffset}</em>}
                        </div>
                        <div className="execution-context-timeline-main">
                          <div className="execution-context-timeline-heading">
                            <span className="execution-context-source-pill">{getExecutionContextSourceLabel(row.source)}</span>
                            <strong>{row.service}</strong>
                          </div>
                          <div className="execution-context-timeline-message">{getExecutionContextTimelineMessage(row)}</div>
                          {(row.workflowLogId || row.environmentId || row.logStreamName) && (
                            <div className="execution-context-timeline-metadata">
                              {row.workflowLogId && <span>log {row.workflowLogId}</span>}
                              {row.environmentId && <span>env {row.environmentId}</span>}
                              {row.logStreamName && <span>{row.logStreamName}</span>}
                            </div>
                          )}
                          {rawData && (
                            <details className="execution-context-raw">
                              <summary>Raw data</summary>
                              {memoryUsage && (
                                <div className={`execution-context-memory-usage ${memoryUsage.isCritical ? 'critical' : ''}`}>
                                  {memoryUsage.items.map((item) => (
                                    <div key={item.label} className="execution-context-memory-usage-item">
                                      <span>{item.label}</span>
                                      <strong>{item.value}</strong>
                                      {item.detail && <em>{item.detail}</em>}
                                    </div>
                                  ))}
                                </div>
                              )}
                              <pre>{rawData}</pre>
                            </details>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default WorkflowLogExecutionContextDrawer;
